import { useTranslation } from "react-i18next";
import type { SkillMode } from "~/api/conversationTypes";
import { useSaveDocumentBody } from "~/api/queries";
import { ABILITY, type Document } from "~/api/types";
import type { DocumentPorts } from "~/features/conversations/ports/editorPorts";

/**
 * One way for the document AI layer to write into the document (W8 §B.6, §B.7).
 *
 * Every apply in this wave — a skill card, an Executive Digest, a comment-thread transform —
 * goes through here, so there is exactly one answer to "where did that text come from and how
 * did it get in". kms had two: `applyAIResult` for most of it and one raw `tr.insertText` in
 * the comment thread, which is why that one path inserted markdown as literal characters.
 *
 * There are two write paths and they are chosen by capability, not by preference:
 *
 *  1. **The actions port** — an in-editor splice through the editor's own markdown parser.
 *     Immediate, range-accurate, and the only path that can honour a selection. Needs an
 *     editable editor, so it is available while EDITING a classic document.
 *  2. **`documents.sculpt`** (`compose` + `providedMarkdown`, act lane) — the platform's
 *     deterministic whole-body write, which works while merely READING and takes a
 *     server-side checkpoint so the change can be reverted. It cannot express a range, so it
 *     serves append and replace-document only.
 *
 * A v2 document has no server path: `SculptService` refuses it with `apply_lane_unsupported`
 * before it inserts an op row. It does have path 1 whenever its editor is mounted — W6.1 gave
 * `@docit/editor-react` a host api, so the port writes into the CRDT through the engine's own
 * attributed write path. A v2 document with no collaboration token has neither, and that is a
 * refusal with a sentence, never a button that quietly does nothing.
 */

export interface ApplyRequest {
  document: Document;
  markdown: string;
  mode: SkillMode;
  ports: DocumentPorts;
  /** What to stamp on the write, for the reader who asks later where a paragraph came from. */
  agent: string;
}

export type ApplyResult = { ok: true } | { ok: false; reason: string };

export function useApplyToDocument() {
  const { t } = useTranslation();
  const save = useSaveDocumentBody();

  const apply = async (request: ApplyRequest): Promise<ApplyResult> => {
    const markdown = request.markdown.trim();
    if (markdown.length === 0) {
      return { ok: false, reason: t("Nothing to write") };
    }
    if (request.document.abilities[ABILITY.documentUpdate] !== true) {
      return { ok: false, reason: t("You don't have edit access to this document") };
    }

    const { actions, selection, unavailableReason } = request.ports;
    const author = { kind: "ai" as const, agent: request.agent };

    // ── The range modes. Only the port can express a range. ────────────────
    if (request.mode === "replace_selection" || request.mode === "insert") {
      const current = selection?.getSelection() ?? null;
      if (!actions?.canWrite || !current) {
        return {
          ok: false,
          reason:
            unavailableReason ??
            (current
              ? t("The document isn't ready to edit")
              : t("Select text in the document first")),
        };
      }
      const wrote = await actions.applyAtRange(
        markdown,
        { from: current.from, to: current.to },
        request.mode === "insert" ? { insertOnly: true, author } : { author },
      );
      return wrote ? { ok: true } : { ok: false, reason: t("Couldn't write to the document") };
    }

    // ── Append / replace the whole document. ───────────────────────────────
    if (actions?.canWrite) {
      const end = actions.documentEnd();
      const range = request.mode === "append" ? { from: end, to: end } : { from: 0, to: end };
      const wrote = await actions.applyAtRange(
        markdown,
        range,
        request.mode === "append" ? { insertOnly: true, author } : { author },
      );
      if (wrote) {
        return { ok: true };
      }
    }

    // The lane that has no server write path at all says so, rather than trying and failing.
    if (unavailableReason && request.document.editorLane === "v2") {
      return { ok: false, reason: unavailableReason };
    }

    const body =
      request.mode === "append"
        ? `${request.document.text.trimEnd()}\n\n${markdown}`.trim()
        : markdown;
    try {
      const outcome = await save.mutateAsync({ documentId: request.document.id, markdown: body });
      // A proposal is a real outcome, not a failure — but it is NOT in the document yet, and
      // saying "written to the document" about it would be untrue.
      return outcome.status === "proposed"
        ? { ok: false, reason: t("Staged as a proposal — this document is collaboratively edited") }
        : { ok: true };
    } catch (error) {
      return {
        ok: false,
        reason: error instanceof Error ? error.message : t("Couldn't write to the document"),
      };
    }
  };

  return { apply, busy: save.isPending };
}

/**
 * Why a write cannot happen, before anybody clicks. `null` means it can.
 *
 * The rule from §D.0, in one function: an affordance whose port is missing renders DISABLED
 * WITH THIS REASON — never hidden, and never enabled-but-inert.
 */
export function writeBlockedReason(
  document: Document,
  ports: DocumentPorts,
  t: (key: string) => string,
): string | null {
  if (document.abilities[ABILITY.documentUpdate] !== true) {
    return t("You don't have edit access to this document");
  }
  if (document.editorLane === "v2") {
    // Since W6.1 the v2 lane HAS a write target — `EditorV2HostApi` carries `getSelection` /
    // `applyAtRange` / `documentEnd` / `findText`, and `documentPorts` binds them — so being
    // on this lane is no longer a refusal by itself. What still refuses is a v2 document with
    // no live editor: with no collaboration token the host shows the last projected text
    // read-only rather than an editor that would discard every keystroke, and there is
    // genuinely nowhere for a write to land. The ports are the ones who know which it is.
    if (ports.actions?.canWrite) {
      return null;
    }
    return (
      ports.unavailableReason ??
      t(
        "The new editor isn't connected on this host, so there is nowhere to apply changes — open this document in the classic editor.",
      )
    );
  }
  return null;
}

/**
 * The same question for a write that must land on a RANGE — a comment transform, an
 * edit-selection skill.
 *
 * These have no server-side fallback: `documents.sculpt` writes a whole body and cannot
 * express "replace exactly these words", so a range write needs the editor port and nothing
 * else will do. Hence a second, stricter answer rather than one blurred check that would
 * either disable append (which works while reading) or promise a range write that cannot
 * happen.
 */
export function rangeWriteBlockedReason(
  document: Document,
  ports: DocumentPorts,
  t: (key: string) => string,
): string | null {
  const blocked = writeBlockedReason(document, ports, t);
  if (blocked) {
    return blocked;
  }
  if (!ports.actions?.canWrite || !ports.findText) {
    return ports.unavailableReason ?? t("The document isn't ready to edit");
  }
  return null;
}
